"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useTranslations } from "next-intl";
import { usePathname } from "@/i18n/navigation";
import { createClient } from "@/lib/supabase/client";
import { useAuth } from "../../auth/auth-context";
import { useModalZIndex } from "@/lib/use-modal-z-index";

interface ScheduleContextValue {
  /** Set of "eventId-attractionId" composite keys */
  savedSetKeys: Set<string>;
  /** Toggle a set in/out of schedule. Shows auth modal if not logged in. */
  toggleScheduleItem: (
    festivalId: number,
    eventId: number,
    attractionId: number,
    attractionName: string
  ) => void;
  isLoading: boolean;
}

const ScheduleContext = createContext<ScheduleContextValue>({
  savedSetKeys: new Set(),
  toggleScheduleItem: () => {},
  isLoading: false,
});

function ScheduleAuthModal({
  festivalName,
  onClose,
}: {
  festivalName: string;
  onClose: () => void;
}) {
  const t = useTranslations("Auth");
  const tFestival = useTranslations("Festival");
  const zIndex = useModalZIndex();
  const pathname = usePathname();

  const params = new URLSearchParams();
  params.set("redirect", pathname);

  const signupUrl = `/auth/signup?${params.toString()}`;
  const loginUrl = `/auth/login?${params.toString()}`;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm"
      style={{ zIndex }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm border border-neutral-800 bg-surface px-8 py-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Corner markers */}
        <div className="absolute -top-1 -left-1 h-3 w-3 border-t border-l border-acid" />
        <div className="absolute -top-1 -right-1 h-3 w-3 border-t border-r border-acid" />
        <div className="absolute -bottom-1 -left-1 h-3 w-3 border-b border-l border-acid" />
        <div className="absolute -bottom-1 -right-1 h-3 w-3 border-b border-r border-acid" />

        <h2 className="mb-2 text-center font-mono text-sm font-bold tracking-wider text-white">
          {tFestival("buildYourSchedule")}
        </h2>
        <p className="mb-6 text-center text-sm text-neutral-400">
          {tFestival("signUpToSaveSchedule", { festival: festivalName })}
        </p>

        <a
          href={signupUrl}
          rel="nofollow"
          className="mb-3 block w-full rounded bg-acid py-2.5 text-center font-mono text-sm font-bold text-black transition-opacity hover:opacity-90"
        >
          {t("signUp")}
        </a>

        <p className="text-center font-mono text-xs text-neutral-500">
          {t("alreadyHaveAccount")}{" "}
          <a
            href={loginUrl}
            rel="nofollow"
            className="text-acid transition-colors hover:text-white"
          >
            {t("logIn")}
          </a>
        </p>
      </div>
    </div>
  );
}

export function ScheduleProvider({
  festivalId,
  festivalName,
  children,
}: {
  festivalId: number;
  festivalName: string;
  children: React.ReactNode;
}) {
  const { user } = useAuth();
  const [savedSetKeys, setSavedSetKeys] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const supabase = createClient();

  // Load schedule on mount when user is logged in
  useEffect(() => {
    if (!user) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSavedSetKeys(new Set());
      return;
    }
    setIsLoading(true);
    supabase
      .rpc("get_user_festival_schedule", { p_festival_id: festivalId })
      .then(({ data }) => {
        if (data) {
          const keys = new Set<string>();
          for (const row of data as {
            event_id: number;
            attraction_id: number;
          }[]) {
            keys.add(`${row.event_id}-${row.attraction_id}`);
          }
          setSavedSetKeys(keys);
        }
        setIsLoading(false);
      });
  }, [user, festivalId, supabase]);

  const toggleScheduleItem = useCallback(
    (
      festivalId: number,
      eventId: number,
      attractionId: number,
      _attractionName: string
    ) => {
      if (!user) {
        setShowAuthModal(true);
        return;
      }

      const key = `${eventId}-${attractionId}`;
      const isSaved = savedSetKeys.has(key);

      // Optimistic update
      setSavedSetKeys((prev) => {
        const next = new Set(prev);
        if (isSaved) next.delete(key);
        else next.add(key);
        return next;
      });

      if (isSaved) {
        supabase
          .from("user_festival_schedules")
          .delete()
          .eq("user_id", user.id)
          .eq("event_id", eventId)
          .eq("attraction_id", attractionId)
          .then(({ error }) => {
            if (error) {
              setSavedSetKeys((prev) => new Set(prev).add(key));
            }
          });
      } else {
        supabase
          .from("user_festival_schedules")
          .insert({
            user_id: user.id,
            festival_id: festivalId,
            event_id: eventId,
            attraction_id: attractionId,
          })
          .then(({ error }) => {
            if (error) {
              setSavedSetKeys((prev) => {
                const next = new Set(prev);
                next.delete(key);
                return next;
              });
            }
          });
      }
    },
    [user, savedSetKeys, supabase]
  );

  return (
    <ScheduleContext.Provider
      value={{
        savedSetKeys,
        toggleScheduleItem,
        isLoading,
      }}
    >
      {children}
      {showAuthModal && (
        <ScheduleAuthModal
          festivalName={festivalName}
          onClose={() => setShowAuthModal(false)}
        />
      )}
    </ScheduleContext.Provider>
  );
}

export function useScheduleBuilder() {
  return useContext(ScheduleContext);
}
